import { Business, BusinessFormData } from '../types';
import { apiClient } from './apiClient';

// Business Service
export class BusinessService {
  // Create a new business
  static async createBusiness(businessData: BusinessFormData): Promise<Business> {
    const response = await apiClient.post('/businesses', businessData);
    return response.data;
  }

  // Get all businesses
  static async getAllBusinesses(): Promise<Business[]> {
    const response = await apiClient.get('/businesses');
    return response.data;
  }

  // Get business by ID
  static async getBusinessById(businessId: string): Promise<Business> {
    const response = await apiClient.get(`/businesses/${businessId}`);
    return response.data;
  }

  // Get business by phone number
  static async getBusinessByPhoneNumber(phoneNumber: string): Promise<Business> {
    const response = await apiClient.get(`/businesses/phone/${phoneNumber}`);
    return response.data;
  }

  // Get businesses by category
  static async getBusinessesByCategory(category: string): Promise<Business[]> {
    const response = await apiClient.get(`/businesses/category/${category}`);
    return response.data;
  }

  // Get businesses by active status
  static async getBusinessesByActive(active: boolean): Promise<Business[]> {
    const response = await apiClient.get(`/businesses/active/${active}`);
    return response.data;
  }

  // Update business
  static async updateBusiness(businessId: string, businessData: Partial<BusinessFormData>, vendorPhone?: string): Promise<Business> {
    const headers: any = {};
    if (vendorPhone) {
      headers['X-Vendor-Phone'] = vendorPhone;
    }
    const response = await apiClient.put(`/businesses/${businessId}`, businessData, { headers });
    return response.data;
  }

  // Delete business
  static async deleteBusiness(businessId: string, vendorPhone?: string): Promise<void> {
    const headers: any = {};
    if (vendorPhone) {
      headers['X-Vendor-Phone'] = vendorPhone;
    }
    await apiClient.delete(`/businesses/${businessId}`, { headers });
  }

  // Get business count
  static async getBusinessCount(): Promise<number> {
    const response = await apiClient.get('/businesses/count');
    return response.data;
  }

  // Get all businesses for a vendor by phone number
  static async getBusinessesByVendorPhoneNumber(phoneNumber: string): Promise<Business[]> {
    const response = await apiClient.get(`/businesses/vendor/${phoneNumber}`);
    return response.data;
  }

  // Get business with themes and inventory
  static async getBusinessWithDetails(businessId: string): Promise<{
    business: Business;
    themes: any[];
    inventory: any[];
  }> {
    const response = await apiClient.get(`/businesses/${businessId}/details`);
    return response.data;
  }

  // Find nearby businesses
  static async findNearbyBusinesses(
    latitude: number,
    longitude: number,
    radiusKm: number = 10.0
  ): Promise<{
    latitude: number;
    longitude: number;
    radiusKm: number;
    count: number;
    businesses: Business[];
  }> {
    const response = await apiClient.get('/businesses/nearby', {
      params: { latitude, longitude, radiusKm },
    });
    return response.data;
  }

  // Geocode an address to get latitude and longitude
  static async geocodeAddress(address: string): Promise<{
    address?: string;
    latitude?: number;
    longitude?: number;
    error?: string;
    message?: string;
    suggestion?: string;
  }> {
    const response = await apiClient.post('/businesses/geocode', { address });
    return response.data;
  }
}

export default BusinessService;
